// ============================================================================
// Date Formatting
// ============================================================================

/**
 * Format a date as a short date string.
 * Example: "Feb 8, 2026"
 */
export function formatShortDate(date: Date | number, locale?: string): string {
  const d = typeof date === 'number' ? new Date(date) : date;
  return new Intl.DateTimeFormat(locale, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(d);
}

/**
 * Format a date as a short date + time string.
 * Example: "Apr 4, 2026, 17:46"
 */
export function formatShortDateTime(
  date: Date | number,
  locale?: string,
): string {
  const d = typeof date === 'number' ? new Date(date) : date;
  return new Intl.DateTimeFormat(locale, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(d);
}

/**
 * Format a date with weekday in short form.
 * Example: "Sun, Feb 8"
 */
export function formatWeekdayShortDate(
  date: Date | number,
  locale?: string,
): string {
  const d = typeof date === 'number' ? new Date(date) : date;
  return new Intl.DateTimeFormat(locale, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  }).format(d);
}

// ============================================================================
// Time Formatting
// ============================================================================

/**
 * Format a date/timestamp as 24h time (HH:MM).
 */
export function formatTime24h(date: Date | number, locale?: string): string {
  const d = typeof date === 'number' ? new Date(date) : date;
  return new Intl.DateTimeFormat(locale, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(d);
}

/**
 * Format a time range, e.g. "09:00 – 10:00".
 */
export function formatTimeRange(
  start: Date | number,
  end: Date | number,
  locale?: string,
): string {
  return `${formatTime24h(start, locale)} – ${formatTime24h(end, locale)}`;
}

// ============================================================================
// Date + Time Formatting Combined
// ============================================================================

/**
 * Format a date + time combination for booking display.
 * Example: "Sunday, Feb 8 · 09:00"
 */
export function formatDateWithTime(
  date: Date | number,
  locale?: string,
): string {
  const d = typeof date === 'number' ? new Date(date) : date;
  return `${new Intl.DateTimeFormat(locale, {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
  }).format(d)} · ${formatTime24h(d, locale)}`;
}

// ============================================================================
// Percent Formatting
// ============================================================================

/**
 * Format a decimal value as a percentage string.
 * Example: formatPercent(0.856) → "85.6%"
 */
export function formatPercent(value: number): string {
  return `${(value * 100).toFixed(1)}%`;
}

// ============================================================================
// Short month + day (no year)
// ============================================================================

/**
 * Format a date as short month + day without year.
 * Example: "Feb 8"
 */
export function formatMonthDay(date: Date | number, locale?: string): string {
  const d = typeof date === 'number' ? new Date(date) : date;
  return new Intl.DateTimeFormat(locale, {
    month: 'short',
    day: 'numeric',
  }).format(d);
}

/**
 * Format a date as month + year.
 * Example: "February 2026"
 */
export function formatMonthYear(date: Date | number, locale?: string): string {
  const d = typeof date === 'number' ? new Date(date) : date;
  return new Intl.DateTimeFormat(locale, {
    month: 'long',
    year: 'numeric',
  }).format(d);
}

// ============================================================================
// Relative Date Formatting
// ============================================================================

/**
 * Format a timestamp as a locale-aware relative date string.
 * Uses Intl.RelativeTimeFormat for proper i18n.
 * Examples: "today", "yesterday", "3 days ago", "2 weeks ago", "5 months ago", "1 year ago"
 */
export function formatRelativeDate(timestamp: number, locale?: string): string {
  const now = Date.now();
  const diff = now - timestamp;
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));

  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });

  if (days === 0) return rtf.format(0, 'day');
  if (days === 1) return rtf.format(-1, 'day');
  if (days < 7) return rtf.format(-days, 'day');
  if (days < 30) return rtf.format(-Math.floor(days / 7), 'week');
  if (days < 365) return rtf.format(-Math.floor(days / 30), 'month');
  return rtf.format(-Math.floor(days / 365), 'year');
}

// ============================================================================
// Pet Age / Weight Formatting
// ============================================================================

/**
 * Format a pet age (float years) into a human-readable string using i18n unit labels.
 *
 * @param age - Age as a float (e.g. 2.5 = 2 years 6 months)
 * @param yrUnit - Localized year abbreviation (e.g. "yr")
 * @param moUnit - Localized month abbreviation (e.g. "mo")
 * @returns Formatted string like "2 yr 6 mo", "3 yr", "4 mo", or "<1m"
 */
export function formatPetAge(
  age: number | undefined,
  yrUnit: string,
  moUnit: string,
): string {
  if (age === undefined || age === 0) return '—';
  const years = Math.floor(age);
  const months = Math.round((age - years) * 12);
  if (years > 0 && months > 0) return `${years} ${yrUnit} ${months} ${moUnit}`;
  if (years > 0) return `${years} ${yrUnit}`;
  if (months > 0) return `${months} ${moUnit}`;
  return `<1${moUnit}`;
}

/**
 * Format a pet weight (float kg) into a human-readable string using i18n unit labels.
 *
 * @param weight - Weight in kg (e.g. 4.3 = 4 kg 300 g)
 * @param kgUnit - Localized kg abbreviation (e.g. "kg")
 * @param gUnit - Localized gram abbreviation (e.g. "g")
 * @returns Formatted string like "4 kg 300 g", "12 kg", "800 g", or "—"
 */
export function formatPetWeight(
  weight: number | undefined,
  kgUnit: string,
  gUnit: string,
): string {
  if (weight === undefined || weight === 0) return '—';
  const kg = Math.floor(weight);
  const grams = Math.round(((weight - kg) * 1000) / 100) * 100;
  if (kg > 0 && grams > 0) return `${kg} ${kgUnit} ${grams} ${gUnit}`;
  if (kg > 0) return `${kg} ${kgUnit}`;
  if (grams > 0) return `${grams} ${gUnit}`;
  return '—';
}
